import getRouterConfig from "@hooks/useRouter";
import analyticsAdapter from "@features/analytics/adapters";
import {
  showSpinner,
  stopSpinner,
  setToastMessage,
  reportError,
  closeDock,
  updateMarketingItem,
  addMarketingItem,
  clickTab,
  clearFormFields,
  setConfirmationDock,
} from "@actions/global";
import {
  getUserSegments,
  getConfigurationDetails,
  getPromotionOptions,
  getFileOptions,
  getCTIDOptions,
} from "@interfaces/config";
import {
  trackBatchCall,
  isLastBatchCallUpToDate,
  invalidateBatchCall,
} from "@interfaces/batch-call";
import {
  loadBanners,
  loadBannersStart,
  loadBanner,
  setBannerPositions,
} from "@features/banners/actions";
import { setCTIDAssets } from "@actions/import";

//constants
import { BASE_PAGE } from "@constants/index";
// utilities
import { googleAnalyticsEventSelection } from "@components/shared/google-analytics-events";
import { convertISODateTime, getNowISO } from "@utils/time-helpers";
import { preSelection, setSearchFilter } from "@utils/interface-helpers";
import { concurrencyBatchCall } from "@utils/browser-helpers";
import {
  naturalSort,
  reduceArrayOfObjToObj,
  setArrayOfObjsWithKey,
} from "@utils/array-helpers";
import { timeEnrichment } from "@reducers/_utils/reducer-helpers";
import { getArrItemByKey } from "@utils/search-helpers";
import { getStore, AppStoreLike, AppDispatch, RootState } from "@store";
import { calculateOffsetLimit } from "@utils/pagination-helpers";
import { userErrorMessage } from "@utils/custom-errors";
import { isEmptyObject } from "@utils/object-helpers";
// adapters
import sypiBannersAdapter from "@features/banners/adapters";
import commercialBannersAdapter from "@features/banners/adapters/commercial-banners";
import type { BannersAdapter, BannerPosition, BannerDisplayConfig, EnrichedBanner, Banner } from "./types";

type Store = AppStoreLike;
type BannerThunk<R = void> = (
  dispatch: AppDispatch,
  getState: () => RootState
) => Promise<R>;

export const bannersInit = (): BannerThunk => async (dispatch, getState) => {
  const store: Store = { dispatch, getState };
  store.dispatch(loadBannersStart());
  await getConfigurationDetails(store);
  getPromotionOptions(store);
  getFileOptions(store);
  getUserSegments(store);
  getCTIDOptions(store);
  await transformBannerPositions(store);
  await loadBannerDisplay({
    status: "live",
    pageLimit: 25,
    pageNumber: 1,
    page_order: "banner_id",
    reverse: true,
  })(dispatch, getState);
};

export const getBannersAdapter = async (
  store: Store = getStore(),
  importingBRC: boolean | null = null
): Promise<BannersAdapter> => {
  const { isCommercialClient } = store.getState().config;
  if (typeof importingBRC === "boolean") {
    return importingBRC ? commercialBannersAdapter : sypiBannersAdapter;
  } else
    return isCommercialClient ? commercialBannersAdapter : sypiBannersAdapter;
};

export const transformBannerPositions = async (store: Store): Promise<void> => {
  const bannersAdapter = await getBannersAdapter(store);
  let bannerPositions: any[] = await bannersAdapter.fetchPositions();
  let consolidatedPositions: Record<string, any> = bannerPositions.reduce((acc: Record<string, any>, curVal: any) => {
    let { position, platform_id, height, width, position_label } = curVal;
    if (acc.hasOwnProperty(position)) {
      let existingPosition = acc[position];
      if (
        !(
          existingPosition.height === height && existingPosition.width === width
        )
      ) {
        acc[position] = {
          ...curVal,
          diverge: true,
          menuLabel: `${position_label} ${platform_id === 9 ? "Apple" : "Android"
            }`,
        };
        acc[`.${position}`] = {
          ...existingPosition,
          diverge: true,
          menuLabel: `${existingPosition.menuLabel} ${existingPosition.platform_id === 9 ? "Apple" : "Android"
            }`,
        };
      } else {
        acc[position] = { ...existingPosition, platform_id: null };
      }
    } else {
      acc[position] = { ...curVal, menuLabel: position_label, diverge: false };
    }
    return acc;
  }, {} as Record<string, any>);
  consolidatedPositions = Object.keys(consolidatedPositions).map(
    (key) => consolidatedPositions[key.replace(/^\./, "")]
  );
  store.dispatch(
    setBannerPositions(naturalSort(consolidatedPositions, "menuLabel"))
  );
};

export const searchBannersByFilter = (
  status: string,
  search: string,
  startDate: string | null,
  stopDate: string | null,
  positionName: string | null,
  platformId: number | null,
  pageLimit: number,
  pageOrder: string,
  pageReversed: boolean,
  pageNumber: number = 1
): BannerThunk => async (dispatch, getState) => {
  const store: Store = { dispatch, getState };
  store.dispatch(loadBannersStart());

  invalidateBatchCall(BASE_PAGE.BANNER, store);
  let bannerConfigs = {
    ...setSearchFilter(
      status,
      search,
      startDate,
      stopDate,
      "toDBDateTimeString"
    ),
    ...(positionName ? { position_name: positionName } : {}),
    ...(platformId ? { platform_id: platformId } : {}),
    pageLimit: pageLimit,
    page_order: pageOrder,
    reverse: pageReversed,
    pageNumber: pageNumber,
  };
  let storeValues = store.getState();
  let total_status =
    bannerConfigs.status === "live" ? "live_total" : "expired_total";
  let setSelected = storeValues.banners[bannerConfigs.status];
  const bannersAdapter = await getBannersAdapter(store);
  let bannerSet = await bannersAdapter.fetchAllBanners(
    calculateOffsetLimit(bannerConfigs, storeValues.banners[total_status])
  );
  let updatedBannerSet = preSelection(
    setSelected,
    "banner_id",
    bannerSet.banners,
    bannerEnrichmentBeforeView
  );
  let bannersFinal = {
    [bannerConfigs.status]: updatedBannerSet,
    [total_status]: bannerSet.total,
  };
  store.dispatch(loadBanners(bannersFinal));
  // store.dispatch(stopSpinner());
};

export const loadBannerDisplay = (bannerConfigs: BannerDisplayConfig): BannerThunk => async (dispatch, getState) => {
  const store: Store = { dispatch, getState };
  store.dispatch(loadBannersStart());
  store.dispatch(showSpinner());
  invalidateBatchCall(BASE_PAGE.BANNER, store);
  let storeValues = store.getState();
  let total_status =
    bannerConfigs.status === "live" ? "live_total" : "expired_total";
  let setSelected = storeValues.banners[bannerConfigs.status];
  const bannersAdapter = await getBannersAdapter(store);
  let bannerSet = await bannersAdapter.fetchAllBanners(
    calculateOffsetLimit(bannerConfigs, storeValues.banners[total_status])
  );
  let updatedBannerSet = preSelection(
    setSelected,
    "banner_id",
    bannerSet.banners,
    bannerEnrichmentBeforeView
  );
  let bannersFinal = {
    [bannerConfigs.status]: updatedBannerSet,
    [total_status]: bannerSet.total,
  };
  store.dispatch(loadBanners(bannersFinal));
  store.dispatch(stopSpinner());
};

export const fetchBannerMetrics = (
  bannerList: any[],
  status: string
): BannerThunk => async (dispatch, getState) => {
  const store: Store = { dispatch, getState };
  let timeID = trackBatchCall(BASE_PAGE.BANNER, store);
  bannerList = bannerList.map((banner) => banner.banner_id);
  let bannerMetrics = await concurrencyBatchCall(
    bannerList,
    analyticsAdapter.fetchBannerStat,
    5,
    true
  );
  bannerMetrics = bannerMetrics?.reduce((acc: any, curVal: any) => {
    acc[curVal.val] = curVal.results.bannerCountList[0];
    return acc;
  }, {});
  let updatedBannerList = store
    .getState()
    .banners[
    status
  ].map((banner: any) => (banner.count === undefined && typeof bannerMetrics[banner.banner_id] !== "undefined" ? { ...banner, count: bannerMetrics[banner.banner_id] } : banner));
  updatedBannerList = setArrayOfObjsWithKey(updatedBannerList, "count", "-");
  if (isLastBatchCallUpToDate(timeID, BASE_PAGE.BANNER)) {
    store.dispatch(loadBanners({ [status]: updatedBannerList }));
  }
};

export const setBanner = (id: string | number, action: string): BannerThunk => async (dispatch, getState) => {
  const store: Store = { dispatch, getState };
  store.dispatch(showSpinner());
  let storeContents = store.getState();
  let { tabSelected } = storeContents.bannerAllView;
  let { banner_id } = storeContents.bannerNewView;
  let banner_status = tabSelected === "liveScheduled" ? "live" : "expired";
  let bannerList = storeContents.banners[banner_status];
  // if banners are already loaded in redux store look up
  const bannersAdapter = await getBannersAdapter(store);
  let bannerDetails =
    bannerList.length !== 0
      ? getArrItemByKey("banner_id", banner_id || id, bannerList)
      : null;

  if (!bannerDetails) {
    bannerDetails = await bannersAdapter.fetchIndividualBanner(id);
  }
  bannerDetails.script_type =
    bannerDetails?.script_type === "Promo"
      ? "Offer"
      : bannerDetails?.script_type === "Promos"
        ? "Offers"
        : bannerDetails?.script_type;
  bannerDetails = bannerEnrichmentBeforeView(bannerDetails);
  store.dispatch(
    loadBanner({
      ...bannerDetails,
      banner_id: action === "duplicate" ? "" : bannerDetails.banner_id,
      ...(bannerDetails.status !== "live"
        ? { position: {}, selectedPositions: [bannerDetails.position] }
        : {}),
    })
  );
  googleAnalyticsEventSelection("Load", {
    id: bannerDetails.banner_id,
    payload: bannerDetails,
    category: "Banners",
  });
  store.dispatch(stopSpinner());
  if (action === "duplicate") {
    store.dispatch(closeDock());

    // @ts-expect-error - history is not typed in getRouterConfig
    const { history } = getRouterConfig();
    store.dispatch(
      setToastMessage(
        "Loading Banner...",
        history ? history.push("/banner_new") : null
      )
    );
  }
};

export const updateBanner = (banner: any, action: string): BannerThunk => async (dispatch, getState) => {
  const store: Store = { dispatch, getState };
  store.dispatch(closeDock());
  store.dispatch(showSpinner());
  let expireAction = action === "expire";

  // When expiring from the list view, banner may be just the ID string
  if (expireAction && typeof banner === "string") {
    const bannersAdapter = await getBannersAdapter(store);
    let result = await bannersAdapter.patchBanner(banner, {
      stop_date: convertISODateTime(getNowISO()).toDBDateTimeString,
      status: "Cancelled",
    });
    if (result.success || result.banner) {
      let updatedBanner = result.banner;
      let clickCount = await analyticsAdapter.fetchBannerStat(
        updatedBanner.banner_id
      );
      updatedBanner = {
        ...bannerEnrichmentBeforeView(updatedBanner),
        count: clickCount.error ? "-" : clickCount.results.bannerCountList[0],
      };
      updatedBanner = setArrayOfObjsWithKey([updatedBanner], "count", "-")[0];
      let expiredList = store.getState().banners.expired;
      if (expiredList.length !== 0) {
        store.dispatch(addMarketingItem("banner", updatedBanner, "expired"));
      }
      store.dispatch(updateMarketingItem("banner", updatedBanner, "expire"));
      store.dispatch(clickTab("expired"));
      googleAnalyticsEventSelection("Expired", {
        id: updatedBanner.banner_id,
        payload: updatedBanner,
        category: "Banners",
      });
      store.dispatch(stopSpinner());
      store.dispatch(clearFormFields());
      // @ts-expect-error - history is not typed in getRouterConfig
      const { history } = getRouterConfig();
      store.dispatch(
        setToastMessage(
          "Your banner changes have been saved.",
          history ? history.push("/banners") : null
        )
      );
    } else {
      store.dispatch(stopSpinner());
      store.dispatch(reportError(userErrorMessage(action, "banner", result)));
    }
    return;
  }

  banner.script_type =
    banner.script_type === "Offer"
      ? "Promo"
      : banner.script_type === "Offers"
        ? "Promos"
        : banner.script_type;
  let addedPositions = banner.selectedPositions;
  if (
    // on edit original position was removed
    !expireAction &&
    banner.hasOwnProperty("position") &&
    isEmptyObject(banner?.position) &&
    banner?.selectedPositions?.length > 0
  ) {
    banner.position = addedPositions.shift();
  }
  const bannersAdapter = await getBannersAdapter(store);
  let result = expireAction
    ? await bannersAdapter.patchBanner(banner.id, {
      stop_date: convertISODateTime(getNowISO()).toDBDateTimeString,
      status: "Cancelled",
    })
    : await bannersAdapter.patchBanner(
      banner.banner_id,
      (({
        banner_id,
        selectedImage,
        imageDetails,
        position,
        formerPosition,
        ...otherKeys
      }) => ({ ...otherKeys }))(bannerEnrichmentBeforeSend(banner))
    );
  if (result.success || result.banner) {
    let updatedBanner = result.banner;
    const { banner_id, status } = updatedBanner;
    let clickCount = await analyticsAdapter.fetchBannerStat(
      updatedBanner.banner_id
    );
    updatedBanner = {
      ...bannerEnrichmentBeforeView(updatedBanner),
      count: clickCount.error ? "-" : clickCount.results.bannerCountList[0],
    };
    updatedBanner = setArrayOfObjsWithKey([updatedBanner], "count", "-")[0];
    if (expireAction || status === "expired") {
      let expiredList = store.getState().banners.expired;
      if (expiredList.length !== 0) {
        store.dispatch(addMarketingItem("banner", updatedBanner, "expired"));
      }
      store.dispatch(updateMarketingItem("banner", updatedBanner, "expire"));
      store.dispatch(clickTab("expired"));
      googleAnalyticsEventSelection("Expired", {
        id: updatedBanner.banner_id,
        payload: updatedBanner,
        category: "Banners",
      });
    } else {
      let setSelected =
        store.getState().bannerAllView.tabSelected === "liveScheduled"
          ? "live"
          : "expired";
      if (
        setSelected === "expired" &&
        (!updatedBanner?.stop_date ||
          (convertISODateTime(updatedBanner?.stop_date)?.toDateTime ?? 0) > getNowISO())
      ) {
        let liveList = store.getState().banners.live;
        if (liveList.length !== 0) {
          store.dispatch(addMarketingItem("banner", updatedBanner, "live"));
        }
        store.dispatch(
          updateMarketingItem("banner", updatedBanner, "expire", "expired")
        );
        store.dispatch(clickTab("liveScheduled"));
      } else {
        store.dispatch(
          updateMarketingItem("banner", updatedBanner, "update", setSelected)
        );
      }
      googleAnalyticsEventSelection("Update", {
        id: updatedBanner.banner_id,
        payload: updatedBanner,
        category: "Banners",
      });
    }
    if (addedPositions && addedPositions.length > 0) {
      saveBanners(banner)(dispatch, getState);
    } else {
      store.dispatch(stopSpinner());
      store.dispatch(clearFormFields());
      // @ts-expect-error - history is not typed in getRouterConfig
      const { history } = getRouterConfig();
      store.dispatch(
        setToastMessage(
          "Your banner changes have been saved.",
          history ? history.push("/banners") : null
        )
      );
    }
  } else {
    store.dispatch(stopSpinner());
    // sentence formatting for update and expire to use -ing
    store.dispatch(reportError(userErrorMessage(action, "banner", result)));
  }
};

export const saveBanners = (bannerDetails: any): BannerThunk => async (dispatch, getState) => {
  const store: Store = { dispatch, getState };
  store.dispatch(showSpinner());
  bannerDetails.script_type =
    bannerDetails.script_type === "Offer"
      ? "Promo"
      : bannerDetails.script_type === "Offers"
        ? "Promos"
        : bannerDetails.script_type;
  let { selectedPositions, position } = bannerDetails;
  let success = false,
    result: Record<string, any> | string = {};
  const bannersAdapter = await getBannersAdapter(store);
  // on duplicate live banner
  if (!isEmptyObject(position) && selectedPositions.length === 0) {
    selectedPositions.push(position);
  }
  for (let index in selectedPositions) {
    let position = selectedPositions[index];
    let bannerEnrichment = bannerEnrichmentBeforeSend({
      ...bannerDetails,
      position: position,
    });
    result = await bannersAdapter.postNewBanner(bannerEnrichment)

    if (typeof result === "object" && (result?.success || result?.banner) ) {
      success = true;
      let newBanner = result.banner;
      newBanner = {
        ...bannerEnrichmentBeforeView(newBanner),
        count: 0,
        status: "scheduled",
      };
      store.dispatch(addMarketingItem("banner", newBanner, "live"));
      googleAnalyticsEventSelection("Create", {
        id: newBanner.banner_id,
        payload: newBanner,
        category: "Banners",
      });
      store.dispatch(clickTab("liveScheduled"));
      store.dispatch(clearFormFields());
    } else {
      store.dispatch(reportError(userErrorMessage("create", "banner", result as string)));
    }
  }
  store.dispatch(stopSpinner());
  if (success) {
    // @ts-expect-error - history is not typed in getRouterConfig
    const { history } = getRouterConfig();
    store.dispatch(
      setToastMessage(
        "Your banner changes have been saved.",
        history ? history.push("/banners") : null
      )
    );
  }
};

const bannerPositionsByKey = (store: Store = getStore()): Record<string, BannerPosition> =>
  reduceArrayOfObjToObj(store.getState().bannerPositions, "position");

export const bannerEnrichmentBeforeView = (item: any): EnrichedBanner => {
  let { script_value, position_name, segment_ids, image_code } = item;
  let bannerPositions = bannerPositionsByKey();
  let bannerPositionObj = bannerPositions[position_name];
  return {
    ...item,
    ...timeEnrichment(item, "view"),
    script_value_label: script_value,
    script_value: script_value,
    position: bannerPositionObj || {},
    position_label:
      (bannerPositionObj && bannerPositionObj.position_label) || "Other",
    segment_ids: segment_ids || "",
    segment_ui: Boolean(segment_ids),
    image: image_code,
    selectedImage: {
      url: image_code,
    },
  };
};

export const bannerEnrichmentBeforeSend = (bannerDetails: any): Record<string, any> => {
  let {
    selectedImage,
    platform_id,
    position,
    inputISOStartDate,
    script_type,
    script_value,
    title,
    segment_ids,
    supplemental_data,
  } = bannerDetails;
  bannerDetails.inputISOStartDate = inputISOStartDate || getNowISO();
  return {
    script_type: script_type,
    script_value: script_value,
    label: title,
    title: title,
    segment_ids: segment_ids,
    supplemental_data: supplemental_data,
    platform_id: platform_id,
    position_name: position.position,
    ...timeEnrichment(bannerDetails, "send"),
    ...(selectedImage && selectedImage.url.startsWith("blob")
      ? {
        image_data: selectedImage.base64,
      }
      : {
        image_code: selectedImage.url,
      }),
  };
};
export const getBannerLink = (image_code: string): string => {
  return window.location.hostname.includes("dev")
    ? `https://mobile-dev.gpshopper.com/img/md5/${image_code}/300/300`
    : `https://api.sypi.app/img/md5/${image_code}/300/300`;
};

export const getCTIDBanners = (
  ctid: string,
  isBrcClient: boolean = false
): BannerThunk => async (dispatch, getState) => {
  const store: Store = { dispatch, getState };
  store.dispatch(showSpinner());
  const bannerPositions = store
    .getState()
    .bannerPositions?.map((bannerPosition: BannerPosition) =>
      bannerPosition?.position?.toLowerCase()
    );
  const bannersAdapter = await getBannersAdapter(store, isBrcClient);
  let result = await bannersAdapter.importAllBanners(ctid);
  if (result && result?.banners) {
    const assets = {
      new: result.banners
        .filter((banner: Banner) =>
          bannerPositions.includes(banner?.position_name?.toLowerCase())
        )
        .map((banner: Banner) => ({
          ...banner,
          url: banner?.image_code,
          status: "",
        })),
    };
    store.dispatch(setCTIDAssets(assets, ctid));
  } else {
    store.dispatch(
      reportError(userErrorMessage("fetch", "assets", result?.error || ""))
    );
  }
  store.dispatch(stopSpinner());
};

export const importBanner = (bannerDetails: any): BannerThunk => async (dispatch, getState) => {
  const store: Store = { dispatch, getState };
  store.dispatch(closeDock());
  store.dispatch(showSpinner());
  // @ts-expect-error - history is not typed in getRouterConfig
  const { history } = getRouterConfig();
  const { inputISOStartDate, inputISOStopDate } = bannerDetails;
  let bannerEnrichment = bannerEnrichmentBeforeSend({
    inputISOStartDate: inputISOStartDate,
    inputISOStopDate: inputISOStopDate,
    ...bannerDetails,
    position: { position: bannerDetails.position_name },
    selectedImage: { url: bannerDetails.image_code },
    status: "scheduled",
  });
  const bannersAdapter = await getBannersAdapter(store);
  const result = await bannersAdapter.postNewBanner(bannerEnrichment);
  if (result && result.success) {
    let newBanner = result.banner;
    newBanner = {
      ...bannerEnrichmentBeforeView(newBanner),
      count: 0,
      status: "scheduled",
    };
    store.dispatch(addMarketingItem("banner", newBanner, "live"));
    googleAnalyticsEventSelection("Create", {
      id: newBanner.banner_id,
      payload: newBanner,
      category: "Banners",
    });
    store.dispatch(clickTab("liveScheduled"));
    store.dispatch(clearFormFields());
    store.dispatch(
      setToastMessage(
        "Banner have been imported.",
        history ? history.push("/banners") : null
      )
    );
  } else {
    store.dispatch(reportError(userErrorMessage("import", "banner", result)));
  }
  store.dispatch(stopSpinner());
};
